<script setup lang="ts">
    import SubmitConfirmation from '@/components/ui/SubmitConfirmation.ce.vue';
    import { type VenueTyping } from '@/typings/widget';
    import BackButton from './BackButton.ce.vue';

    defineProps<{
        venue: VenueTyping | null
    }>();

    const emit = defineEmits<{(event: 'backToPath' | 'backToMain')}>();

    const { t } = useI18n();

    const projectTypeId: Ref<number | null> = ref(null);
    const successSend: Ref<boolean> = ref(false);

    const fixedTypes = [
        {
            label: t('widget.register.event-types.wedding'),
            value: 1,
        },
        {
            label: t('widget.register.event-types.birthday'),
            value: 4,
        },
        {
            label: t('widget.register.event-types.baby-shower'),
            value: 5,
        },
        {
            label: t('widget.register.event-types.company'),
            value: 6,
        },
        {
            label: t('widget.register.event-types.meeting'),
            value: 7,
        },
        {
            label: t('widget.register.event-types.condolence'),
            value: 8,
        },
        {
            label: t('widget.register.event-types.other'),
            value: 9,
        },
    ];
</script>

<template>
    <div class="allstars-register">
        <SubmitConfirmation
            v-if="successSend"
            mode="wisk"
            @back-to-main="emit('backToMain')"
        />
        <template v-else>
            <div class="allstars-register__back">
                <BackButton @click="emit('backToPath')" />
            </div>
            <div class="allstars-register__form">
                <form
                    method="post"
                    :action="venue?.allStartsQuoteUrl"
                >
                    <div class="allstars-register__form__content">
                        <h1>
                            {{ t('widget.register.title') }}
                        </h1>
                        <span>
                            {{ t('widget.register.subtitle') }}
                        </span>
                        <input
                            type="hidden"
                            name="submit"
                            value="register"
                        >
                        <select
                            v-model="projectTypeId"
                            required
                            name="projecttypeId"
                        >
                            <option value="null">
                                {{ `${t('widget.register.choose')}*` }}
                            </option>
                            <option
                                v-for="option in fixedTypes"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <div class="allstars-register__form__content__row">
                            <input
                                :placeholder="`${t('widget.form.your-name')}*`"
                                type="text"
                                name="name"
                                required
                            >
                            <input
                                :placeholder="`${t('widget.form.your-surname')}*`"
                                type="text"
                                name="secondName"
                            >
                        </div>
                        <div
                            v-if="projectTypeId === 1"
                            class="allstars-register__form__content__row"
                        >
                            <input
                                id="partnerName"
                                :placeholder="`${t('widget.form.partner-name')}*`"
                                type="text"
                                name="partnerName"
                            >
                            <input
                                id="partnerSecondName"
                                :placeholder="`${t('widget.form.partner-surname')}*`"
                                type="text"
                                name="partnerSecondName"
                            >
                        </div>
                        <input
                            v-if="[6,7].includes(Number(projectTypeId))"
                            id="companyName"
                            :placeholder="t('widget.form.company-name')"
                            type="text"
                            name="companyName"
                        >
                        <input
                            :placeholder="`${t('widget.form.email-2')}*`"
                            type="email"
                            name="email"
                            required
                        >
                        <input
                            :placeholder="`${t('widget.form.phone')}*`"
                            type="text"
                            name="phone"
                            required
                            maxlength="15"
                            minlength="10"
                            onkeyup="this.value=this.value.replace(/[^\d\.]/g, '');"
                            pattern="\d{10}"
                        >
                        <input
                            v-if="projectTypeId === 9"
                            id="event_name"
                            :placeholder="t('widget.form.other')"
                            type="text"
                            name="event_name"
                        >
                        <input
                            id="g-recaptcha-response"
                            type="hidden"
                            name="g-recaptcha-response"
                            value="widget"
                        >
                        <button
                            type="submit"
                            class="btn btn--primary"
                        >
                            <span>
                                {{ t('widget.buttons.start') }}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.allstars-register {
    position: relative;
    block-size: 100%;
    inline-size: 100%;
    -ms-overflow-style: none;
    overflow-y: auto;
    padding-block-end: var(--spacing-12);
    scroll-behavior: smooth;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @include min-width(lg) {
        padding-block-end: 0;
    }

    &__back {
        position: relative;
        inset-block-start: var(--spacing-01);
        inset-inline-start: var(--spacing-01);
        margin-block: var(--spacing-04);
        padding-inline: var(--widget-inner-gutter);

        @include min-width(md) {
            position: sticky;
        }
    }

    &__form {
        display: flex;
        flex-direction: column;

        &__content {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: var(--spacing-03);
            gap: var(--spacing-03);
            inline-size: 100%;
            margin-inline: auto;
            max-inline-size: 488px;
            padding-block: var(--spacing-01) var(--spacing-10);

            @include min-width(lg) {
                padding-block-start: 0;
            }

            h1, > span {
                text-align: center;
            }

            > span {
                display: block;
                margin-block-end: var(--spacing-04);
            }

            input,
            select {
                box-sizing: border-box;
                padding: var(--spacing-03);
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-form-input);
                block-size: var(--size-11);
                color: var(--text-secondary);
                font-family: var(--font-primary);
                font-size: var(--font-medium);
                font-weight: var(--font-weight-light);
                inline-size: 100%;
                line-height: 1;
                outline: none;

                &::placeholder {
                    color: var(--text-placeholder);
                }

                &:hover,
                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }

                @include min-width(md) {
                    block-size: var(--size-12);
                }

                @include min-width(lg) {
                    block-size: var(--size-13);
                }
            }

            select {
                appearance: none;
            }

            .btn {
                margin-block-start: var(--spacing-04);
            }

            &__row {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                gap: var(--spacing-03);

                @include min-width(md) {
                    flex-direction: row;
                }

                > * {
                    flex: 1;
                }
            }
        }
    }

    :deep(.formkit-actions) {
        position: absolute;
        z-index: 100;

        .book-it-btn {
            position: fixed;
            z-index: 100;
            inline-size: calc(100% - var(--size-16));
            inset-block-end: var(--spacing-05);
            inset-inline-end: 50%;
            transform: translateX(50%);

            button {
                color: var(--text-primary-invert);
                font-size: var(--font-medium);
                font-weight: var(--font-weight-medium);
                inline-size: 100%;
                letter-spacing: var(--letter-spacing-0);
                line-height: var(--line-height-150);
            }

            @include min-width(md) {
                inline-size: auto;
            }

            @include min-width(lg) {
                inset-inline-end: var(--spacing-07);
                transform: initial;
            }
        }
    }
}
</style>
